// ENG-5053: detect MCP children that should be running but aren't, and
// trigger a session restart so they come back online.
//
// Why this exists: an MCP child can die under a live parent claude
// (transient error during init, OOM, downstream service blip during a
// tool call). Claude Code's MCP transport advertises that it'll
// respawn a dead child on the next request — but in practice we've
// seen agents go silent for hours with a missing MCP and no recovery
// (Stirling, 2026-05-13, Xero MCP). The agent keeps reporting "X is
// still warming up" indefinitely. Killing the tmux session forces the
// manager's ensure-session pass to respawn it, which gets the missing
// child back.
//
// This module is the *presence* counterpart to stale-mcp-reaper.ts
// (which handles env rotation — child alive but holding a stale
// credential) and orphan-channel-mcp-reaper.ts (which handles dead
// parents — child alive but its parent claude is gone). The gap they
// don't cover is the one this fills: parent claude alive, declared
// MCP child absent, no rotation pending.
//
// Pure helpers exposed for tests:
//   • findMissingMcpServers() — given parsed ps rows + the agent's
//     codeName + parsed .mcp.json, return the declared mcpServers
//     keys that have ZERO matching live children belonging to this
//     agent's claude.
//
// Side-effecting:
//   • reapMissingMcpSessions() — runs `ps`, checks presence, applies
//     the cold-start grace window, and calls back to stopSession()
//     so the manager's ensure-session pass respawns the agent.

import { execFileSync } from 'node:child_process';
import { hardBoundedExecFile } from './bounded-exec.js';
import { parsePsRows, type PsRow } from './orphan-channel-mcp-reaper.js';
import {
  classifyUnanswerablePane,
  summarizeUnanswerablePane,
  type UnanswerablePaneKind,
} from './claude-dialogs.js';
import {
  DEFAULT_ROTATION_GRACE_MS,
  classifyRotation,
  clearStaleRotation,
  findMcpChildrenForAgent,
  getRotationRecordedAt,
  type McpConfig,
} from './stale-mcp-reaper.js';

/**
 * Cold-start grace window — how long after a session starts we wait
 * before flagging a missing MCP. The Composio MCP spawns via npx which
 * can take 20-30s to install on a fresh box; without this grace a
 * brand-new session would be repeatedly killed mid-init. (Xero used to
 * share this failure mode but is now CLI-bundled and starts instantly,
 * ENG-7579 — the grace still covers Composio and any other npx server.)
 */
export const DEFAULT_COLD_START_GRACE_MS = 90_000;

/**
 * ENG-5279: maximum consecutive presence-reaper restarts before we give
 * up on a permanently-broken declared MCP. Set to 3 to match the
 * agent-thrash-monitor threshold — if we keep restarting past that
 * point, the thrash detector pages Slack with a false alarm for a
 * failure the reaper can't fix by restarting (orphan in .mcp.json from
 * a pre-ENG-5277 install, an unpublished MCP package, a transiently
 * failing npx install that's now permanently broken, etc.).
 */
export const MAX_PRESENCE_RESTART_ATTEMPTS = 3;

/**
 * ENG-6480: circuit-breaker / backoff for a persistently-FLAPPING MCP.
 *
 * The ENG-5279 `attempts` cap above only catches a server that NEVER comes
 * live (an orphaned config) — because the per-tick reset loop zeroes
 * `attempts` the moment a key is seen live on ANY poll. A *crash-looping*
 * MCP (starts → dies → starts → dies, e.g. sterling's xero on 2026-06-14)
 * resets `attempts` every cycle and so never reaches give-up → it bounces the
 * whole tmux session on an unbounded loop (≥5 restarts/window, tripping the
 * ENG-5286 flapping alarm, and starving in-flight admin restart acks →
 * `agent_restart_stuck`).
 *
 * The fix is a per-(codeName, serverKey) restart HISTORY that — unlike
 * `attempts` — survives a brief green blip (pruned only by age, and cleared
 * only after a *sustained* live stretch). From it we derive:
 *   1. an escalating backoff interval that SKIPS a restart that would land
 *      too soon after the previous one (so the restart RATE drops below the
 *      flapping threshold instead of firing every poll); and
 *   2. the SAME give-up terminal (onGiveUp → mark-unhealthy → dropped from
 *      .mcp.json) once the key flaps past a ceiling — so a crash-looper ends
 *      in the identical "broken" state as an orphan, not a third notion.
 *
 * `BACKOFF_AFTER_RESTARTS` is tied to the ENG-5279 cap on purpose: the first
 * `MAX_PRESENCE_RESTART_ATTEMPTS` restarts stay immediate (`backoffMs = 0`),
 * so the orphan path (which gives up at the cap before backoff is ever
 * consulted) and existing transient-recovery behaviour are byte-for-byte
 * unchanged; backoff only engages in the crash-loop case where `attempts`
 * keeps resetting.
 */
export const BACKOFF_AFTER_RESTARTS = MAX_PRESENCE_RESTART_ATTEMPTS;
/** Base backoff once past BACKOFF_AFTER_RESTARTS; doubles per extra restart. */
export const BACKOFF_BASE_MS = 60_000;
/** Ceiling on the escalating backoff interval. */
export const BACKOFF_MAX_MS = 30 * 60_000;
/** Rolling window over which restarts are counted for backoff + rate give-up. */
export const BACKOFF_WINDOW_MS = 60 * 60_000;
/**
 * Restarts within the window that trip the rate-based give-up (mark-unhealthy).
 * Set to 2× the cap: a crash-looper escalates BASE→2×→4× between restarts and
 * reaches this in ~15 min, then gets disabled — coherent with the ENG-5286
 * flapping alarm rather than a separate notion of "broken".
 */
export const BACKOFF_GIVE_UP_RESTARTS = 2 * MAX_PRESENCE_RESTART_ATTEMPTS;
/**
 * Continuous-liveness dwell that clears the restart history (genuine
 * recovery). A brief green blip in a crash-loop never reaches this — its
 * liveness stretch is broken by the next miss — so the history (and thus the
 * backoff/breaker) survives blips, while a truly-recovered MCP resets ~here.
 */
export const BACKOFF_STABLE_RESET_MS = 5 * 60_000;

/**
 * ENG-8062: absolute wall-clock floor beyond which a CONTINUOUSLY-missing key
 * is treated as genuinely dead, overriding the ENG-8013 storm-awareness
 * suppression. Storm-awareness (`currentGenerationExternallyStarted`) stops a
 * healthy channel being quarantined as collateral of a respawn storm by
 * re-basing the dwell + skipping the attempts increment on externally-started
 * generations — but a channel that is truly dead (its child never once comes
 * live, e.g. stirling's spawn-lock deadlock) never accrues dwell and so churns
 * the session forever. A healthy channel comes live at least once inside any
 * plausible storm generation, which resets `continuouslyMissingSince`; only a
 * channel that is missing on EVERY poll for this long is genuinely dead, so the
 * floor is set generously (15 min) to keep the storm-awareness guarantee intact
 * while still letting a dead channel reach give-up / quarantine / backoff.
 */
export const DEAD_CHANNEL_MS = 15 * 60_000;

/**
 * Per-(codeName, serverKey) state for the cap logic.
 *
 * `attempts` counts distinct restart cycles where the server never
 * came back live — NOT distinct polls. CodeRabbit on PR #1167: pre-fix
 * the counter incremented on every tick the server was missing, so a
 * slow respawn (3+ polls during a single in-flight restart) would
 * exhaust the cap before three actual session generations had been
 * observed. The `lastAttemptedSessionStartedAt` field is the per-
 * generation gate — we only increment when this differs from the
 * current `sessionStartedAt` (i.e. the manager spawned a fresh
 * session since our last attempt), and after incrementing we record
 * the current generation so subsequent polls within the same
 * generation are no-ops for the counter.
 *
 * `gaveUpLogged` records whether we've already emitted the give-up
 * audit line so subsequent ticks stay silent.
 *
 * Cleared per-agent by `clearPresenceReaperState()` when the manager
 * sees `.mcp.json` content change — that's the signal that something
 * external fixed the config and the give-up state is stale.
 */
interface PresenceReaperKeyState {
  attempts: number;
  lastSeenLiveAt: number | null;
  lastAttemptedSessionStartedAt: number | null;
  gaveUpLogged: boolean;
  /**
   * ENG-5932: epoch ms when this key was first observed continuously missing
   * in the current stretch (null when live). Drives the quarantine dwell gate
   * — an OPTIONAL channel isn't a quarantine candidate until it's been missing
   * for `quarantineDwellMs` AFTER the cold-start grace. Reset to null the
   * moment the key is seen live again (so a channel that recovers doesn't
   * carry a stale dwell clock).
   */
  firstMissingAt: number | null;
  /**
   * ENG-5932: gates the once-per-key quarantine log line + onQuarantine
   * callback, mirroring `gaveUpLogged`. Reset on liveness.
   */
  quarantineLogged: boolean;
  /**
   * ENG-6480: epoch-ms timestamps of session restarts the reaper triggered on
   * THIS key's behalf, within the rolling `BACKOFF_WINDOW_MS`. Unlike
   * `attempts` it is NOT reset by a brief green blip (only pruned by age, and
   * cleared after a sustained live stretch — see `stableLiveSince`), so it
   * catches a crash-looping MCP that comes live each cycle. Drives the
   * escalating backoff interval and the rate-based give-up.
   */
  restartHistory: number[];
  /**
   * ENG-6480: epoch ms when the key's CURRENT continuous-live stretch began
   * (null while missing). Once it has been continuously live for
   * `BACKOFF_STABLE_RESET_MS` the restart history is cleared (genuine
   * recovery). A single green blip in a crash-loop never reaches the dwell —
   * the next miss nulls this — so the breaker survives blips.
   */
  stableLiveSince: number | null;
  /**
   * ENG-8062: epoch ms when the key entered its CURRENT continuously-missing
   * stretch (null while live). Unlike {@link firstMissingAt} this is NEVER
   * re-based by the ENG-8013 storm-awareness path — it resets only when the key
   * is actually seen live again. Once `now - continuouslyMissingSince` exceeds
   * {@link DEAD_CHANNEL_MS} the key is genuinely dead and escapes the
   * externally-started-generation suppression (counts attempts + can quarantine)
   * so a dead channel stops churning the session indefinitely.
   */
  continuouslyMissingSince: number | null;
  /**
   * ENG-8062: gates the once-per-stretch "genuinely dead, overriding storm-
   * awareness" log line so it doesn't repeat every poll. Reset on liveness.
   */
  deadChannelLogged: boolean;
  /**
   * ENG-10500: gates the once-per-stretch "given up but NOT quarantined - no
   * declared stdio MCP is live" log line so it doesn't repeat every poll.
   * Reset on liveness, and deliberately SEPARATE from `quarantineLogged`:
   * withholding must not consume the one-shot quarantine gate, or a session
   * that recovers could never quarantine a channel that really is dead.
   */
  quarantineWithheldLogged: boolean;
  /**
   * ENG-6480: true while the key is currently given up on (either cause:
   * the ENG-5279 attempts cap OR the rate-based flap ceiling). Reset on
   * liveness. Read by {@link givenUpMcpServerKeys} so the resume reconciler
   * sees a rate-given-up key as broken too, not just attempts-given-up ones.
   */
  gaveUp: boolean;
}

const presenceReaperState = new Map<string, PresenceReaperKeyState>();

/**
 * ENG-8225: the outcome of a stale-mcp-reaper rotation, from the point of view
 * of the child it SIGTERM'd.
 *
 *   'respawned-on-tool-call'       — the child came back on its own, because the
 *                                    agent happened to call a tool on that
 *                                    server. The ENG-5344 happy path.
 *   'respawned-after-prompt-probe' — the child came back only after we armed
 *                                    ENG-7429's prompt bind-probe to MANUFACTURE
 *                                    that tool call.
 *   'never-respawned'             — the prompt-probe budget was exhausted with
 *                                    the child still absent. Escalated.
 *
 * Deliberately NOT named "respawned-on-timeout": nothing here respawns a child
 * directly. A stdio MCP child is a subprocess of Claude Code on the session's
 * own stdio pipes, and `findMcpChildrenForAgent` only counts a child whose ppid
 * chain reaches a `claude` ancestor — so a manager-spawned child would be both
 * invisible to this reaper and unreachable by any tool call. The only lever the
 * manager actually has is to cause the next tool call, which is what these
 * values describe. See ENG-8225's re-framing.
 */
export type RotationRespawnOutcome =
  | 'respawned-on-tool-call'
  | 'respawned-after-prompt-probe'
  | 'never-respawned';

/**
 * ENG-8225: how many prompt bind-probes we arm for a single stalled rotation
 * before escalating. The probe is best-effort (it asks the agent to exercise its
 * tool surface; a busy or wedged session may not get to it promptly), so a small
 * budget rather than one shot — the same reasoning as ENG-7429's own
 * PROMPT_BIND_PROBE_ATTEMPTS budget.
 */
export const MAX_ROTATION_PROMPT_PROBES = 3;

/**
 * ENG-8225: minimum spacing between prompt-probe arms for the same stalled
 * rotation. ENG-7429's probe budget spans ~60s of force-due re-checks at the
 * manager's 10s poll cadence, so arming on every poll would burn the whole
 * ENG-8225 budget while the FIRST probe was still in flight and escalate a
 * rotation that was about to recover. 30s gives each arm a fair chance.
 */
export const ROTATION_PROMPT_PROBE_INTERVAL_MS = 30_000;

/**
 * ENG-8225: per-(agent, serverKey) state for a rotation whose grace window
 * expired with the child still absent. Separate from
 * {@link PresenceReaperKeyState} because it tracks the ROTATION's fate, not the
 * key's restart budget: it is created by a rotation, cleared by the child coming
 * back, and irrelevant to a key that was never rotated.
 *
 * Because the fate it tracks belongs to ONE rotation while the map key belongs
 * to the server, every entry carries {@link RotationStallState.rotationRecordedAt}
 * — read that field's doc before touching this map. An entry is valid only for
 * the rotation whose stamp it holds; anything else is a leftover.
 */
interface RotationStallState {
  /**
   * ENG-8225: the SIGTERM timestamp of the rotation this state describes, as
   * reported by `getRotationRecordedAt`. This is the state's LIFETIME ANCHOR:
   * the map is keyed by (agent, serverKey), which outlives any one rotation, so
   * the stamp is the only thing that distinguishes "this budget belongs to the
   * rotation I am looking at" from "this is a leftover from a rotation that is
   * over". A stamp mismatch means the entry is garbage and must be replaced,
   * NOT consulted — see the read site in `reapMissingMcpSessions`.
   */
  rotationRecordedAt: number;
  /** Prompt bind-probes armed for this stalled rotation so far. */
  probeAttempts: number;
  /** Epoch-ms of the most recent arm, for the {@link ROTATION_PROMPT_PROBE_INTERVAL_MS} spacing. */
  lastProbeAt: number | null;
  /** True once the budget was exhausted and the escalation fired (once-guard). */
  escalated: boolean;
}

const rotationStallState = new Map<string, RotationStallState>();

function stateKey(codeName: string, serverKey: string): string {
  return `${codeName}\x00${serverKey}`;
}

/**
 * Clear every per-server give-up state for `codeName`. Wipes ALL
 * entries for the agent regardless of which keys changed.
 *
 * Prefer {@link clearPresenceReaperStateForKeys} from production code
 * (ENG-5285) — value-only drifts on .mcp.json (e.g. composio rotating
 * a signed proxy URL every ~20 min) shouldn't reset the cap on keys
 * whose presence-classification didn't actually change. This whole-
 * agent wipe was the root cause of Stirling flapping 450× over six
 * days post-ENG-5279: every URL rotation reset the cap before the
 * reaper could give up on the broken xero MCP.
 *
 * Kept exported for tests + any future caller that genuinely needs a
 * full agent-scoped wipe (none in production today).
 */
export function clearPresenceReaperState(codeName: string): void {
  const prefix = `${codeName}\x00`;
  for (const key of presenceReaperState.keys()) {
    if (key.startsWith(prefix)) presenceReaperState.delete(key);
  }
}

/**
 * ENG-5285: clear give-up state only for the named MCP server keys.
 *
 * Call from the manager-worker's `.mcp.json` drift handler with the
 * set of keys that were ADDED or REMOVED relative to the previous
 * .mcp.json. Keys present in both old and new (even if their values
 * changed — e.g. composio rotated a signed URL) must NOT be cleared,
 * because their presence-classification is unchanged and the cap on
 * a permanently-broken MCP must keep accumulating across config
 * rewrites.
 *
 * No-op when `keys` is empty.
 */
export function clearPresenceReaperStateForKeys(
  codeName: string,
  keys: Iterable<string>,
): void {
  for (const key of keys) {
    presenceReaperState.delete(stateKey(codeName, key));
    // ENG-8225: a key that was ADDED or REMOVED from .mcp.json has no in-flight
    // rotation to chase — drop its stall state alongside its restart budget so a
    // re-added server starts with a fresh prompt-probe budget.
    rotationStallState.delete(stateKey(codeName, key));
  }
}

/**
 * Test-only seam: reset all state. Production code should use
 * `clearPresenceReaperState(codeName)` instead — wiping the whole map
 * would lose give-up state for unrelated agents.
 */
export function __resetPresenceReaperStateForTests(): void {
  presenceReaperState.clear();
  rotationStallState.clear();
}

/**
 * ENG-6383 (ENG-6375 Slice C): the set of this agent's MCP server keys the
 * presence-reaper has GIVEN UP on — declared servers still missing after the
 * full restart budget (`attempts >= MAX_PRESENCE_RESTART_ATTEMPTS`). A given-up
 * key is a declared MCP that is genuinely absent, so the safe-resume reconciler
 * must NOT clear a circuit-breaker trip while one is outstanding (it feeds
 * `ResumeHealthSnapshot.mcpPresent` via `deriveMcpPresent`). Read-only.
 */
export function givenUpMcpServerKeys(codeName: string): Set<string> {
  const prefix = `${codeName}\x00`;
  const out = new Set<string>();
  for (const [key, st] of presenceReaperState) {
    // ENG-6480: `gaveUp` covers BOTH give-up causes (attempts cap + rate
    // ceiling); keep the legacy attempts predicate as a belt-and-braces
    // superset for any state populated before `gaveUp` was set this tick.
    if (key.startsWith(prefix) && (st.gaveUp || st.attempts >= MAX_PRESENCE_RESTART_ATTEMPTS)) {
      out.add(key.slice(prefix.length));
    }
  }
  return out;
}

/**
 * Pure: given parsed ps rows + the agent's codeName + parsed
 * `.mcp.json`, return the declared `mcpServers` keys that have ZERO
 * matching live children belonging to this agent's claude.
 *
 * Reuses `findMcpChildrenForAgent` from stale-mcp-reaper so the argv-
 * matching rules and ppid-walk are shared with the existing reapers
 * (one source of truth for "which child belongs to which agent + which
 * server key").
 */
export function findMissingMcpServers(args: {
  rows: PsRow[];
  codeName: string;
  mcpJson: McpConfig | null | undefined;
  /** ENG-6660: rows are from `docker exec ps` in the agent's container - use the
   * relaxed in-container claude-ancestor walk (see findMcpChildrenForAgent). */
  inContainer?: boolean;
}): string[] {
  const { rows, codeName, mcpJson, inContainer } = args;
  const servers = mcpJson?.mcpServers ?? {};
  const declared = Object.keys(servers);
  if (declared.length === 0) return [];

  const missing: string[] = [];
  for (const key of declared) {
    // ENG-5075: skip URL-based remote MCP entries (Streamable HTTP /
    // SSE — anything with a `url` field and no `command`). Claude
    // Code dials those endpoints directly; there's no stdio child
    // process to discover via `ps`. Pre-fix, the reaper iterated
    // every declared server and flagged URL-based ones (e.g. Granola
    // post-ENG-5074) as "missing" every poll cycle → killed the
    // session every ~3 minutes → Scout flapped indefinitely with the
    // signature "declared MCP(s) [granola] have no live children".
    //
    // The presence check is only meaningful for stdio MCPs — those
    // are the ones with a child process that could die under a live
    // parent. URL-based entries can't have that failure mode at the
    // child-process layer (their failure mode is "HTTP request fails
    // at call time", which is the MCP transport's job to handle, not
    // the reaper's).
    const entry = (servers as Record<string, unknown>)[key] as
      | { command?: unknown; url?: unknown }
      | undefined;
    const isStdio = typeof entry?.command === 'string';
    if (!isStdio) continue;

    const live = findMcpChildrenForAgent({
      rows,
      codeName,
      serverKeys: [key],
      mcpJson,
      inContainer,
    });
    if (live.length === 0) missing.push(key);
  }
  return missing;
}

export interface ReapMissingMcpSessionsArgs {
  log: (msg: string) => void;
  codeName: string;
  mcpJson: McpConfig | null | undefined;
  /**
   * When the parent claude session was last started (Date.now() ms).
   * Used to apply the cold-start grace window — we don't flag a
   * missing MCP within `graceMs` of startup, because the npx-delivered
   * Composio MCP can take ~30s to npm-install on first spawn (xero is
   * now CLI-bundled and exempt from this, ENG-7579). Pass null when
   * the session start time is unknown — the reaper then *skips* the
   * agent rather than risk a false-positive restart.
   */
  sessionStartedAt: number | null;
  /**
   * ENG-6486: true when the manager already has a session restart SCHEDULED or
   * DEFERRED for this agent (i.e. `pendingSessionRestarts.has(codeName)`). When
   * a restart is pending, a declared-but-not-yet-live MCP is EXPECTED — the
   * pending restart will spawn it once it fires — so the reaper must not fire
   * again. Pre-fix, a freshly-ADDED channel (e.g. slack added to valai's set)
   * whose convergence restart was deferred by the maintenance window made the
   * reaper re-detect "declared MCP has no live children" every poll; each firing
   * its `onRestart` callback (→ McpReaperRestartsHourly) even though the restart
   * was itself deferred, tripping a FALSE `agent_reaper_flapping` alarm
   * (gccm-host/valai, 2026-06-15, 04:08-04:11). Firing while a restart is
   * already pending achieves nothing (the reaper's own restart defers too), so
   * skipping is strictly correct. Default false → legacy behaviour unchanged.
   */
  restartPending?: boolean;
  /**
   * ENG-8013 (storm-awareness): true when the agent's CURRENT session generation
   * was torn down + respawned by an EXTERNAL cause (anything but this reaper's
   * own mcp-presence recovery restart) - e.g. an integration-change /
   * channel-set-change / day-rollover / wedge / operator restart, or a respawn
   * STORM. When true, a declared-but-missing channel MCP is treated as COLLATERAL
   * of the churn: its "no live child" observation does NOT count toward the
   * give-up / quarantine budget (`attempts` stays put, the quarantine dwell clock
   * re-bases), because the MCP hasn't had a stable session generation to come up
   * in. Only a generation the reaper ITSELF restarted counts as a genuine failed
   * recovery. Default false → legacy behaviour (every generation counts) is
   * byte-for-byte unchanged.
   *
   * This is the durable fix for ENG-8003: an integration-hash respawn storm
   * quarantined Sherlock's HEALTHY Slack MCP by inflating `attempts` one storm
   * generation at a time until the cap tripped, even though Slack was never
   * broken and the reaper's own recovery restarts were deferred by the
   * maintenance window and never ran.
   */
  currentGenerationExternallyStarted?: boolean;
  /** Callback that tears the agent's session down so the manager's
   *  ensure-session pass respawns it on the next tick.
   *
   *  ENG-5547: `ctx.activeKeys` is the set of under-cap missing MCP servers
   *  that prompted *this* restart (the same keys logged on the restart line).
   *  The manager uses its cardinality to decide whether the restart counts
   *  toward the agent-wide circuit breaker: a single persistently-failing
   *  server is isolated by its own ENG-5279 give-up cap (→ marked unhealthy →
   *  dropped from .mcp.json), so it must NOT pause the whole agent; a restart
   *  driven by several servers at once is a genuine storm that should. */
  stopSession: (codeName: string, ctx: { activeKeys: string[] }) => void;
  /** Override the cold-start grace (default: 90s). */
  graceMs?: number;
  /**
   * ENG-5344: post-rotation grace. When stale-mcp-reaper SIGTERMs an MCP
   * child during a credential rotation, the child won't respawn until
   * Claude Code makes the next tool call. Without this grace, the next
   * manager tick (~2s later) sees zero live children for the rotated
   * key and kills the whole tmux session — observed on Stirling at the
   * 20-min xero token rotation cadence (54 false-positive restarts/day).
   *
   * Default: {@link DEFAULT_ROTATION_GRACE_MS} (15s — comfortably above
   * the SIGTERM→SIGKILL grace plus typical respawn latency).
   */
  rotationGraceMs?: number;
  /**
   * ENG-8225: the rotation grace above expired and the rotated child is STILL
   * absent — nothing made the tool call the grace window was banking on. Arm
   * ENG-7429's prompt bind-probe for this agent so the next tool call is
   * MANUFACTURED, and skip the whole-session restart this tick.
   *
   * MUST return whether the probe was actually armed. When it returns false (the
   * `session-tool-probe` flag is off, so arming is inert), or is not wired at
   * all, or throws, the key falls straight through to the pre-ENG-8225 path — a
   * suppressed restart with no probe behind it would be strictly worse than the
   * restart, so this fails OPEN to the legacy behaviour.
   *
   * Called at most once per {@link ROTATION_PROMPT_PROBE_INTERVAL_MS} per key,
   * up to {@link MAX_ROTATION_PROMPT_PROBES} times, then escalates via
   * {@link onRotationRespawnGaveUp}.
   */
  onRotationRespawnStalled?: (
    codeName: string,
    serverKey: string,
    ctx: { attempt: number; maxAttempts: number },
  ) => boolean;
  /**
   * ENG-8225 (AC3): the prompt-probe budget is exhausted and the rotated child
   * never came back — outcome `never-respawned`. Fired EXACTLY ONCE per stalled
   * rotation. Production use: manager-worker marks the integration unhealthy via
   * `/host/integration-health` and opens a `reaper_action` alert via
   * `/host/reaper-event` — the same two escalation paths `onGiveUp`/`onRestart`
   * already use, so this adds no new alerting channel.
   *
   * After escalating, the key is released to the pre-ENG-8225 path (it joins
   * `missing` on this and every later tick) rather than being suppressed
   * forever: the restart budget + ENG-6480 backoff already bound that path, and
   * permanent suppression of a genuinely dead MCP would be worse than a bounded
   * restart. Errors are swallowed — the reaper's correctness must not depend on
   * the callback.
   */
  onRotationRespawnGaveUp?: (
    codeName: string,
    serverKey: string,
    ctx: { attempts: number },
  ) => void;
  /**
   * ENG-8225 (AC2): fired for every observed rotation outcome — including the
   * healthy `respawned-on-tool-call` one — so the three outcomes are countable,
   * not just the failure. Errors are swallowed.
   */
  onRotationRespawnOutcome?: (
    codeName: string,
    serverKey: string,
    outcome: RotationRespawnOutcome,
  ) => void;
  /** ENG-8225: override the prompt-probe budget (default {@link MAX_ROTATION_PROMPT_PROBES}). */
  maxRotationPromptProbes?: number;
  /** ENG-8225: override the arm spacing (default {@link ROTATION_PROMPT_PROBE_INTERVAL_MS}). */
  rotationPromptProbeIntervalMs?: number;
  /** Test seam: substitute the `ps` invocation. */
  runPs?: () => string;
  /**
   * ENG-6660: the agent is Docker-isolated (AGT_ISOLATION=docker). When true,
   * the default `runPs` enumerates the agent's OWN container via
   * `docker exec agt-<codeName> ps` (its MCP children live in the container's PID
   * namespace, invisible to the host), and matching skips the claude-parent-walk
   * (the container is a single-agent boundary). Without this, every declared MCP
   * looks "missing" on the host -> restart loop -> reaper flapping.
   */
  isolated?: boolean;
  /** Test seam: substitute the wall clock for grace-window checks. */
  now?: () => number;
  /**
   * ENG-5292: invoked EXACTLY ONCE per (codeName, serverKey) when the
   * restart cap (ENG-5279) is first exceeded — i.e. the first poll
   * where we'd give up on the key. Gated by the same `gaveUpLogged`
   * flag that prevents duplicate log lines, so subsequent ticks while
   * the key remains given-up don't re-fire.
   *
   * Production use: manager-worker registers a callback that POSTs to
   * `/host/integration-health` with action=mark_unhealthy. The endpoint
   * flips agent_integrations.status, which makes /host/managed-toolkits
   * drop the broken MCP from its response, which causes the next
   * .mcp.json regeneration to omit it cleanly.
   *
   * Errors thrown by the callback are swallowed (logged) so an API
   * outage doesn't break the reaper itself. The give-up is recorded
   * locally regardless — at worst the integration stays in active
   * state on the DB side and the next give-up tick after the API
   * recovers will retry the call (the cap state still trips immediately
   * since `gaveUpLogged` is local to this process).
   */
  onGiveUp?: (codeName: string, serverKey: string) => void;
  /**
   * ENG-5286: invoked every time the reaper actually triggers a session
   * restart (i.e. just before `stopSession()`). Production use: manager-
   * worker registers a callback that POSTs to /host/mcp-reaper-restart
   * so the API can roll up restart counts per agent into a CloudWatch
   * metric + flapping alarm.
   *
   * Errors thrown by the callback are swallowed (logged) — the reaper's
   * own correctness must not depend on the API call succeeding.
   */
  onRestart?: (
    codeName: string,
    args: { activeKeys: string[]; givenUpKeys: string[] },
  ) => void | Promise<void>;
  /**
   * ENG-5932: classify a declared stdio server as 'essential' or 'optional'.
   * Only OPTIONAL servers can be quarantined; ESSENTIAL ones keep the legacy
   * declared-but-dead give-up behaviour. Default: always 'essential' — i.e.
   * quarantine is a strict no-op unless the caller wires a real classifier, so
   * every existing caller and test is byte-for-byte unchanged.
   */
  classifyKey?: (serverKey: string) => 'essential' | 'optional';
  /**
   * ENG-5932: minimum continuous-missing duration (epoch-ms delta, measured
   * from `firstMissingAt`) before an OPTIONAL key becomes a quarantine
   * candidate — a belt-and-braces time floor ON TOP of the restart budget so a
   * channel that's merely slow to respawn isn't quarantined. Default 0 (the
   * dwell gate is satisfied immediately; the restart budget alone governs).
   */
  quarantineDwellMs?: number;
  /**
   * ENG-8062: absolute continuous-missing floor (epoch-ms delta from
   * `continuouslyMissingSince`) past which a key is treated as genuinely dead
   * and overrides the ENG-8013 storm-awareness suppression — so a dead channel
   * reaches give-up / quarantine / backoff instead of churning forever under
   * external respawns. Default {@link DEAD_CHANNEL_MS} (15 min).
   */
  deadChannelMs?: number;
  /**
   * ENG-5932: 'off' | 'shadow' | 'enforce'. Default 'off'.
   *   off     — no quarantine logic at all (legacy behaviour).
   *   shadow  — compute the decision and LOG "would quarantine X", but take no
   *             action (the session still gives up / stays declared-but-dead
   *             exactly as before). For one bake cycle before enforcing.
   *   enforce — additionally fire {@link onQuarantine} so the manager persists
   *             the marker and drops the channel from the provisioned set.
   */
  quarantineMode?: 'off' | 'shadow' | 'enforce';
  /**
   * ENG-5932: fired EXACTLY ONCE per (codeName, serverKey) when an OPTIONAL key
   * is quarantined in enforce mode (gated by `quarantineLogged`). Production
   * use: manager-worker persists the quarantine marker to disk; the next
   * provisioning poll then omits the channel from `.mcp.json`. Errors are
   * swallowed (logged) — the reaper's correctness must not depend on it.
   */
  onQuarantine?: (codeName: string, serverKey: string) => void;
  /**
   * ENG-9932: read the agent's tmux pane, or null when it cannot be read.
   *
   * Consulted at exactly ONE point — immediately before the restart fires, and
   * only once a restart has already been decided on. That placement is
   * deliberate on three counts:
   *
   *   1. COST. A healthy agent never reads its pane, so this adds no per-poll
   *      tmux call to the common path. The capture happens only on the tick we
   *      were about to tear the session down anyway.
   *   2. NO DOUBLE-REPORTING. `channel-input-watchdog` already reports an
   *      unclaimed modal on its own cadence (ENG-9006). Gating only the restart
   *      keeps this from becoming a second, louder report of the same pane on
   *      every poll of a healthy-but-wedged agent.
   *   3. SCOPE. The claim this makes is narrow and checkable — "the restart we
   *      were about to perform cannot succeed" — rather than the much broader
   *      and more failure-prone "this agent is unwell".
   *
   * UNWIRED IS A STRICT NO-OP. Absent (or returning null, or throwing) leaves
   * the pre-ENG-9932 behaviour byte-for-byte unchanged: the restart proceeds.
   * A pane we cannot read must never become a silent suppression of MCP
   * recovery across the fleet, so every failure mode here fails OPEN.
   */
  readPane?: (codeName: string) => string | null;
  /**
   * ENG-9932: fired when a restart was held because the pane is blocked on a
   * prompt no keystroke and no respawn can clear. This is the signal that
   * turns today's two-hour misdiagnosis into a thirty-second one — the reaper
   * is the component that KNOWS a restart is futile, so it is the component
   * that has to say so.
   *
   * Deduped by the caller if it wants; the reaper fires it on each held tick so
   * a stuck pane keeps a live signal rather than decaying into silence after
   * one line. Errors are swallowed — the reaper's correctness must not depend
   * on the callback, matching `onGiveUp` / `onRestart` / `onQuarantine`.
   */
  onPaneBlocked?: (
    codeName: string,
    ctx: {
      kind: UnanswerablePaneKind;
      activeKeys: string[];
      /** Content-FREE fingerprint of the modal block — see
       *  summarizeUnanswerablePane for why this is a hash and not the text.
       *  Null when no focused modal block could be read (the login picker
       *  renders no confirm footer, and its kind is already unambiguous). */
      paneHash: string | null;
      paneLength: number | null;
    },
  ) => void;
}

export interface ReapMissingMcpSessionsResult {
  /** The declared server keys we found missing. May be empty when
   *  everything is healthy, when the session is still in cold-start,
   *  or when sessionStartedAt is unknown. */
  missing: string[];
  /** True iff we actually called stopSession() this invocation. */
  restarted: boolean;
  /** Stable reason code for the no-op path, useful in tests. */
  reason?:
    | 'healthy'
    | 'cold-start'
    | 'session-start-unknown'
    | 'no-declared-servers'
    | 'no-mcp-json'
    | 'restart-pending'
    | 'all-keys-over-cap'
    | 'backoff'
    /** ENG-9932: a restart was due, but the pane is sitting on a prompt that
     *  neither a keystroke nor a respawn can clear, so restarting would only
     *  spend breaker budget re-presenting it. */
    | 'pane-blocked-on-dialog';
  /** ENG-5279: server keys that were missing but skipped because they
   *  hit the consecutive-restart cap. The session would otherwise have
   *  been restarted; we held off. Empty in the common case. */
  givenUp?: string[];
  /** ENG-6480: server keys that were missing and under the give-up ceilings
   *  but whose escalating backoff interval had not yet elapsed — the restart
   *  was held off this tick (it'll fire once the interval passes). Present
   *  only when at least one missing key is backing off. */
  backingOff?: string[];
  /** ENG-5344: server keys that were missing but skipped because they
   *  were rotated by stale-mcp-reaper within the rotation grace window.
   *  Treated as in-flight (will respawn on next tool call), not missing. */
  rotationGraced?: string[];
  /** ENG-8225: keys whose rotation grace EXPIRED with the child still absent and
   *  for which a prompt bind-probe was armed this tick. Excluded from `missing`
   *  — the point of the probe is to avoid the whole-session restart. */
  rotationProbeArmed?: string[];
  /** ENG-8225: keys with a probe already armed and its {@link
   *  ROTATION_PROMPT_PROBE_INTERVAL_MS} spacing not yet elapsed. Also excluded
   *  from `missing` (we're waiting on the probe, not on a restart). */
  rotationProbeWaiting?: string[];
  /** ENG-8225: keys that exhausted the prompt-probe budget this tick — outcome
   *  `never-respawned`, escalated, and released to the legacy restart path. */
  rotationProbeExhausted?: string[];
  /** ENG-8225 (AC2): rotation outcomes observed this invocation, keyed by server
   *  key. Present only when at least one outcome was reached. */
  rotationRespawnOutcomes?: Record<string, RotationRespawnOutcome>;
  /** ENG-5932 (enforce mode): OPTIONAL keys quarantined this invocation —
   *  onQuarantine fired, marker to be persisted by the caller. */
  quarantined?: string[];
  /** ENG-5932 (shadow mode): OPTIONAL keys that WOULD have been quarantined
   *  had enforce mode been on. Logged, no action taken. */
  wouldQuarantine?: string[];
  /** ENG-9932: the kind of blocking pane that held the restart. Present only
   *  alongside reason 'pane-blocked-on-dialog'. */
  paneBlockedOn?: UnanswerablePaneKind;
  /** ENG-8062: keys that have been continuously missing past `deadChannelMs`
   *  and so overrode the ENG-8013 storm-awareness suppression this tick
   *  (counted attempts / became quarantine-eligible despite an externally-
   *  started generation). Present only when at least one key tripped it. */
  genuinelyDead?: string[];
  /** ENG-10500: OPTIONAL keys that reached the give-up terminal and satisfied
   *  the dwell floor, but were NOT quarantined because NO declared stdio MCP
   *  server was live this tick - so the restart budget was exhausted by a
   *  session that could not spawn anything, not by these channels dying.
   *  Present in every mode except 'off'. */
  quarantineWithheld?: string[];
}

/**
 * Run `ps`, find declared MCP servers that have no live children for
 * <codeName>, and trigger a session restart if any are missing AND the
 * cold-start grace has elapsed. Idempotent — repeated calls when a
 * restart is already in flight are harmless (stopSession is a no-op
 * once the tmux session is already gone).
 */
export function reapMissingMcpSessions(
  args: ReapMissingMcpSessionsArgs,
): ReapMissingMcpSessionsResult {
  const {
    log,
    codeName,
    mcpJson,
    sessionStartedAt,
    restartPending = false,
    stopSession,
    graceMs = DEFAULT_COLD_START_GRACE_MS,
    rotationGraceMs = DEFAULT_ROTATION_GRACE_MS,
    onGiveUp,
    onRestart,
    // ENG-8225: unwired callbacks make the prompt-probe path a strict no-op —
    // `onRotationRespawnStalled` absent means "not armed", which falls through to
    // the pre-ENG-8225 restart. Legacy callers and tests are unchanged.
    onRotationRespawnStalled,
    onRotationRespawnGaveUp,
    onRotationRespawnOutcome,
    maxRotationPromptProbes = MAX_ROTATION_PROMPT_PROBES,
    rotationPromptProbeIntervalMs = ROTATION_PROMPT_PROBE_INTERVAL_MS,
    // ENG-5932: defaults make quarantine a strict no-op (classify everything
    // essential, mode off) so legacy callers/tests are unaffected.
    classifyKey = () => 'essential',
    quarantineDwellMs = 0,
    deadChannelMs = DEAD_CHANNEL_MS,
    quarantineMode = 'off',
    onQuarantine,
    isolated = false,
    // ENG-8013: default false → a "no live child" observation is only excluded
    // from the give-up/quarantine budget when the caller positively identifies
    // the current generation as externally-churned. Legacy callers/tests unchanged.
    currentGenerationExternallyStarted = false,
    // ENG-9932: unwired => strict no-op, restart proceeds exactly as before.
    readPane,
    onPaneBlocked,
  } = args;
  const now = args.now ?? Date.now;
  // ENG-6660: under Docker the agent's claude + MCP children live in the
  // container's PID namespace; enumerate THAT (docker exec), not the host.
  const runPs =
    args.runPs ??
    (isolated
      ? // ENG-9781: HARD-bounded, and the distinction is the whole ticket. The
        // previous `execFileSync(..., { timeout: 8_000 })` bounded nothing —
        // Node fires SIGTERM at the deadline and then keeps waiting, so a
        // docker CLI talking to a wedged daemon blocked the manager-worker's
        // event loop and stopped scheduling for EVERY agent on the host
        // (33m46s, twice, on 2026-09-01). This call runs once per isolated
        // agent per poll cycle, so it is the single worst place on the manager
        // for an unbounded subprocess.
        //
        // Throwing on a non-answer is deliberate rather than lazy: the caller
        // already catches, logs and SKIPS the agent for the cycle, which is
        // exactly right. A timeout must not become an empty process list —
        // that reads as "every declared MCP server is missing" and restarts the
        // session, turning a docker hiccup into a fleet-wide restart storm.
        () => {
          const r = hardBoundedExecFile(
            'docker',
            ['exec', `agt-${codeName}`, 'ps', '-eo', 'pid,ppid,args'],
            { timeoutMs: 8_000, log },
          );
          if (r.kind === 'ok') return r.stdout;
          throw new Error(
            r.kind === 'timed-out'
              ? `docker exec ps did not answer within its bound (bounded=${r.bounded})`
              : `docker exec ps failed (status=${r.status}): ${r.message}`,
          );
        }
      : () => execFileSync('ps', ['-eo', 'pid,ppid,args'], { encoding: 'utf-8', timeout: 5_000 }));

  if (!mcpJson?.mcpServers) {
    return { missing: [], restarted: false, reason: 'no-mcp-json' };
  }
  const declaredCount = Object.keys(mcpJson.mcpServers).length;
  if (declaredCount === 0) {
    return { missing: [], restarted: false, reason: 'no-declared-servers' };
  }

  // ENG-6486: a session restart is already scheduled/deferred for this agent.
  // Any declared-but-not-yet-live MCP will be spawned by that pending restart,
  // so flagging it now is a false positive — and worse, our own restart would
  // also be deferred while still firing onRestart (inflating the flapping
  // metric → false agent_reaper_flapping alarm). Skip entirely; the pending
  // restart, followed by the cold-start grace, covers the add→spawn window.
  if (restartPending) {
    return { missing: [], restarted: false, reason: 'restart-pending' };
  }

  // Skip rather than risk a false-positive when we don't know when the
  // session started. The manager-worker passes null for agents whose
  // PersistentSession state hasn't been populated yet.
  if (sessionStartedAt === null) {
    return { missing: [], restarted: false, reason: 'session-start-unknown' };
  }

  // Cold-start grace: skip if the session is still in its grace window.
  if (now() - sessionStartedAt < graceMs) {
    return { missing: [], restarted: false, reason: 'cold-start' };
  }

  let psOutput: string;
  try {
    psOutput = runPs();
  } catch (err) {
    log(`[mcp-presence-reaper] ps invocation failed for '${codeName}': ${(err as Error).message} — skipping`);
    return { missing: [], restarted: false, reason: 'healthy' };
  }

  const rows = parsePsRows(psOutput);
  const missingRaw = findMissingMcpServers({ rows, codeName, mcpJson, inContainer: isolated });

  // ENG-5344: stale-mcp-reaper rotation grace. When stale-reaper SIGTERMs
  // an MCP child during credential rotation, the child won't come back
  // until Claude Code makes its next tool call. Within the grace window,
  // treat "no live children" for that key as in-flight, not missing —
  // pre-fix this was the entire root cause of Stirling's 54 daily false-
  // positive restarts (and matching kills on don/phil/maven).
  const rotationGraced: string[] = [];
  // ENG-8225: the three buckets for a rotation whose grace expired with the child
  // still absent. Keys in `rotationProbeArmed` / `rotationProbeWaiting` are held
  // OUT of `missing` — a prompt bind-probe is the cheap targeted repair, and
  // falling through to `missing` would tear down the whole session, which is the
  // outcome this ticket exists to prevent.
  const rotationProbeArmed: string[] = [];
  const rotationProbeWaiting: string[] = [];
  const rotationProbeExhausted: string[] = [];
  const rotationRespawnOutcomes: Record<string, RotationRespawnOutcome> = {};
  const recordRotationOutcome = (key: string, outcome: RotationRespawnOutcome): void => {
    rotationRespawnOutcomes[key] = outcome;
    if (!onRotationRespawnOutcome) return;
    try {
      onRotationRespawnOutcome(codeName, key, outcome);
    } catch (err) {
      log(
        `[mcp-presence-reaper] onRotationRespawnOutcome callback threw for '${codeName}:${key}' (suppressed; reaper continues): ${(err as Error).message}`,
      );
    }
  };
  const missing: string[] = [];
  for (const key of missingRaw) {
    const rotation = classifyRotation(codeName, key, rotationGraceMs, now);
    if (rotation === 'within-grace') {
      rotationGraced.push(key);
      continue;
    }
    if (rotation === 'none') {
      // ENG-8225: no rotation on record, so any stall state under this key
      // describes a rotation the tracker has already forgotten (the child never
      // came back and the record aged past ROTATION_TRACKING_RETENTION_MS). Drop
      // it here rather than leaving a dead entry in the map — the stamp check
      // below is what makes correctness independent of this cleanup firing, but
      // an entry nothing will ever consult should not linger.
      rotationStallState.delete(stateKey(codeName, key));
      missing.push(key);
      continue;
    }
    // ENG-8225: rotated, grace expired, still no live children. ENG-5344's grace
    // window banked on "the child respawns on the next tool call" — and nothing
    // makes that tool call happen. dwight's `anchor-browser` child died at
    // 16:00:05 and never came back because dwight had no reason to call an
    // anchor-browser tool all evening; the rarer the server, the longer the hole
    // stays open, which inverts the risk.
    //
    // We cannot respawn the child ourselves: a stdio MCP child belongs to Claude
    // Code, on the session's own stdio pipes, and findMcpChildrenForAgent only
    // counts children whose ppid chain reaches a `claude` ancestor — a
    // manager-spawned child would be invisible HERE and unreachable by any tool
    // call. So we cause the next tool call instead, by arming ENG-7429's prompt
    // bind-probe.
    const sk = stateKey(codeName, key);
    // ENG-8225 follow-up: `rotationStallState` is keyed by (agent, serverKey),
    // which outlives any single rotation, so an entry is only meaningful for the
    // rotation that created it. The rotation's SIGTERM timestamp is its identity
    // (grace and retention are both measured from it, and a new SIGTERM
    // overwrites it), so we anchor the entry to that stamp and treat a mismatch
    // as "no state": a NEW rotation always starts with a full probe budget and an
    // unset escalation flag.
    //
    // Why the stamp rather than clearing the entry at each place a rotation could
    // end: the cleanup sites are all conditional on the reaper OBSERVING an
    // intermediate state (a 'within-grace' tick, a 'none' tick, the child seen
    // live). None of those is guaranteed — at a 10s poll cadence against a 15s
    // grace, one slow tick is enough for an 'expired' read to be the FIRST
    // observation of a brand-new rotation, and a stalled child that never returns
    // is never seen live at all. That gap is precisely the leak this fixes:
    // pre-fix, one escalated rotation left `escalated: true` under the key
    // forever, so every later rotation of that server skipped the probe path and
    // went straight to a whole-session restart — the flapping ENG-8225 exists to
    // remove. Comparing identity at the single READ site cannot miss a cycle,
    // whereas adding another flag to remember "this escalation is stale" would
    // just be more unanchored state of the same shape.
    //
    // `?? now()` is unreachable (classifyRotation returned 'expired', which
    // requires a live record) and only avoids a non-null assertion; if it ever
    // did fire, a stamp of `now()` still forces a fresh budget, which is the safe
    // direction.
    const rotationRecordedAt = getRotationRecordedAt(codeName, key) ?? now();
    const priorStall = rotationStallState.get(sk);
    const stall: RotationStallState =
      priorStall && priorStall.rotationRecordedAt === rotationRecordedAt
        ? priorStall
        : {
            rotationRecordedAt,
            probeAttempts: 0,
            lastProbeAt: null,
            escalated: false,
          };
    if (priorStall && priorStall !== stall) {
      log(
        `[mcp-presence-reaper] '${codeName}:${key}' discarding stall state from a previous rotation (rotation=${priorStall.rotationRecordedAt}, probes=${priorStall.probeAttempts}, escalated=${priorStall.escalated}) — this is a NEW rotation (rotation=${rotationRecordedAt}) and gets a full prompt-probe budget (ENG-8225)`,
      );
    }
    rotationStallState.set(sk, stall);
    if (stall.escalated) {
      // Budget already spent and the escalation already fired — hand the key back
      // to the pre-ENG-8225 path rather than suppressing it forever.
      missing.push(key);
      continue;
    }
    const spacingElapsed =
      stall.lastProbeAt === null || now() - stall.lastProbeAt >= rotationPromptProbeIntervalMs;
    if (!spacingElapsed) {
      // A probe is armed and hasn't had its fair chance yet. Waiting is the whole
      // point; don't restart, don't burn another attempt.
      rotationProbeWaiting.push(key);
      continue;
    }
    if (stall.probeAttempts >= maxRotationPromptProbes) {
      // AC3: escalate rather than degrade into an endless session-restart loop.
      stall.escalated = true;
      rotationProbeExhausted.push(key);
      recordRotationOutcome(key, 'never-respawned');
      log(
        `[mcp-presence-reaper] '${codeName}:${key}' rotation outcome=never-respawned — child SIGTERM'd by stale-mcp-reaper never returned after ${stall.probeAttempts} prompt bind-probe(s); escalating (integration marked unhealthy + reaper_action alert) and releasing to the normal restart path (ENG-8225)`,
      );
      if (onRotationRespawnGaveUp) {
        try {
          onRotationRespawnGaveUp(codeName, key, { attempts: stall.probeAttempts });
        } catch (err) {
          log(
            `[mcp-presence-reaper] onRotationRespawnGaveUp callback threw for '${codeName}:${key}' (suppressed; reaper continues): ${(err as Error).message}`,
          );
        }
      }
      missing.push(key);
      continue;
    }
    // Arm the next prompt bind-probe. Fails OPEN: an unwired / disabled / throwing
    // arm means no probe is coming, so suppressing the restart would leave the
    // agent with a dead MCP and nothing working to fix it — worse than today.
    const attempt = stall.probeAttempts + 1;
    let armed = false;
    if (onRotationRespawnStalled) {
      try {
        armed = onRotationRespawnStalled(codeName, key, {
          attempt,
          maxAttempts: maxRotationPromptProbes,
        });
      } catch (err) {
        log(
          `[mcp-presence-reaper] onRotationRespawnStalled callback threw for '${codeName}:${key}' (suppressed; falling back to the pre-ENG-8225 restart path): ${(err as Error).message}`,
        );
        armed = false;
      }
    }
    if (!armed) {
      missing.push(key);
      continue;
    }
    stall.probeAttempts = attempt;
    stall.lastProbeAt = now();
    rotationProbeArmed.push(key);
    log(
      `[mcp-presence-reaper] '${codeName}:${key}' rotation grace expired (${rotationGraceMs}ms) with no live child and no tool call to respawn it — armed prompt bind-probe ${attempt}/${maxRotationPromptProbes} (ENG-7429) instead of restarting the session (ENG-8225)`,
    );
  }
  if (rotationGraced.length > 0) {
    log(
      `[mcp-presence-reaper] '${codeName}': skipping [${rotationGraced.join(', ')}] within rotation grace window (${rotationGraceMs}ms) — child(ren) just SIGTERM'd by stale-mcp-reaper, awaiting respawn on next tool call (ENG-5344)`,
    );
  }
  if (rotationProbeWaiting.length > 0) {
    log(
      `[mcp-presence-reaper] '${codeName}': skipping [${rotationProbeWaiting.join(', ')}] — prompt bind-probe already armed for the stalled rotation, waiting out the ${rotationPromptProbeIntervalMs}ms re-arm interval (ENG-8225)`,
    );
  }

  // ENG-5279: update per-(agent, server) recovery state on EVERY tick so
  // a key that came back to life — even briefly — resets its counter.
  // Walk the full declared stdio set (not just `missing`), so transitions
  // from missing→live clear the attempt count immediately. Without this
  // a flapping MCP would accumulate restarts forever.
  //
  // ENG-5344: use the UNFILTERED `missingRaw` to classify liveness — a
  // rotation-graced key has zero live children right now, so it must
  // not reset the cap counter (which would let a chronically broken MCP
  // never reach give-up if it gets rotated frequently).
  const declared = mcpJson.mcpServers ?? {};
  const liveKeys = new Set<string>();
  for (const key of Object.keys(declared)) {
    const entry = (declared as Record<string, unknown>)[key] as { command?: unknown } | undefined;
    if (typeof entry?.command !== 'string') continue; // stdio-only
    if (!missingRaw.includes(key)) liveKeys.add(key);
  }
  // ENG-8225 (AC2): a live key with a rotation still on record means the rotation
  // COMPLETED — the child is back. Attribute it: if we had to manufacture a tool
  // call with ENG-7429's prompt probe it's `respawned-after-prompt-probe`,
  // otherwise the agent called a tool on its own and ENG-5344's assumption held
  // (`respawned-on-tool-call`). Then forget the rotation, so a later disappearance
  // is treated as a fresh fault rather than this rotation's tail.
  //
  // Runs OUTSIDE the `if (state)` guard below: a key that has been healthy all
  // along has no presenceReaperState entry, and its respawn is exactly the
  // outcome we most want counted.
  for (const key of liveKeys) {
    const sk = stateKey(codeName, key);
    if (classifyRotation(codeName, key, rotationGraceMs, now) === 'none') {
      // ENG-8225 follow-up: no rotation on record, so there is no outcome to
      // attribute — but a stall entry under this key describes a rotation that is
      // definitively over (this is the path a key takes when it goes live only
      // AFTER its record aged past ROTATION_TRACKING_RETENTION_MS, which the
      // pre-fix `continue` skipped, stranding the entry). Drop it.
      rotationStallState.delete(sk);
      continue;
    }
    const stall = rotationStallState.get(sk);
    const viaProbe = (stall?.probeAttempts ?? 0) > 0;
    recordRotationOutcome(
      key,
      viaProbe ? 'respawned-after-prompt-probe' : 'respawned-on-tool-call',
    );
    // One line per completed rotation, carrying the outcome value verbatim so the
    // three-way split is greppable/countable from manager.log alone (AC2). Cheap:
    // the rotation record is cleared immediately below, so this fires once per
    // rotation, not once per poll.
    log(
      viaProbe
        ? `[mcp-presence-reaper] '${codeName}:${key}' rotation outcome=respawned-after-prompt-probe — child is live again after ${stall?.probeAttempts} prompt bind-probe(s)${
            stall?.escalated
              ? ' (recovered AFTER escalation — the earlier never-respawned verdict stands for that window)'
              : ''
          }, no session restart needed (ENG-8225)`
        : `[mcp-presence-reaper] '${codeName}:${key}' rotation outcome=respawned-on-tool-call — child returned on the agent's own next tool call, ENG-5344's assumption held (ENG-8225)`,
    );
    clearStaleRotation(codeName, [key]);
    rotationStallState.delete(sk);
  }
  for (const key of liveKeys) {
    const state = presenceReaperState.get(stateKey(codeName, key));
    if (state) {
      state.attempts = 0;
      state.lastSeenLiveAt = now();
      state.lastAttemptedSessionStartedAt = null;
      state.gaveUpLogged = false;
      // ENG-5932: the key recovered — clear the dwell clock + quarantine log
      // gate so a future failure stretch starts fresh (a channel that comes
      // back to life must not carry a stale firstMissingAt toward quarantine).
      state.firstMissingAt = null;
      state.quarantineLogged = false;
      // ENG-8062: real liveness is the ONLY thing that resets the genuinely-dead
      // clock (storm-awareness never touches it), so a channel that actually
      // recovers sheds its dead-channel status cleanly.
      state.continuouslyMissingSince = null;
      state.deadChannelLogged = false;
      // ENG-10500: the withhold verdict was about THIS missing stretch. A key
      // that came live starts a fresh one, so the line may be said again.
      state.quarantineWithheldLogged = false;
      // ENG-6480: the key is live again — it's no longer given up.
      state.gaveUp = false;
      // ENG-6480: track the START of this continuous-live stretch (only when
      // null; do NOT advance it on every live poll, or the dwell never
      // accrues). Once continuously live past BACKOFF_STABLE_RESET_MS, clear
      // the restart history — that's genuine recovery. A crash-loop's green
      // blip never reaches the dwell because the next miss nulls this below.
      if (state.stableLiveSince === null) state.stableLiveSince = now();
      if (now() - state.stableLiveSince >= BACKOFF_STABLE_RESET_MS) {
        state.restartHistory = [];
      }
    }
  }

  if (missing.length === 0) {
    return {
      missing,
      restarted: false,
      reason: 'healthy',
      rotationGraced: rotationGraced.length > 0 ? rotationGraced : undefined,
      rotationProbeArmed: rotationProbeArmed.length > 0 ? rotationProbeArmed : undefined,
      rotationProbeWaiting: rotationProbeWaiting.length > 0 ? rotationProbeWaiting : undefined,
      rotationProbeExhausted:
        rotationProbeExhausted.length > 0 ? rotationProbeExhausted : undefined,
      rotationRespawnOutcomes:
        Object.keys(rotationRespawnOutcomes).length > 0 ? rotationRespawnOutcomes : undefined,
    };
  }

  // Tally attempts and partition missing into active (under cap) vs
  // given-up (at or over cap). Per-generation guard via
  // `lastAttemptedSessionStartedAt`: only count an attempt when the
  // manager has spawned a fresh session since our last increment for
  // this key. A slow respawn (multiple reaper polls between
  // `stopSession()` and the new session actually coming up) must NOT
  // burn the cap — the manager's stop wrapper is idempotent and
  // those extra calls aren't real restart cycles.
  const nowMs = now();
  const givenUp: string[] = [];
  const active: string[] = [];
  // ENG-6480: missing keys under the give-up ceilings whose escalating backoff
  // interval hasn't elapsed — held off this tick, NOT a restart driver.
  const backingOff: string[] = [];
  // ENG-6480: keys for which THIS tick is a fresh session generation. Only a
  // new generation may append to restartHistory — repeated polls during a
  // single in-flight restart (same sessionStartedAt) must not inflate the
  // window count, exactly like the `attempts` per-generation guard.
  const newGenerationKeys = new Set<string>();
  // ENG-5932: OPTIONAL keys quarantined (enforce) / would-be-quarantined
  // (shadow) this invocation.
  const quarantined: string[] = [];
  const wouldQuarantine: string[] = [];
  // ENG-10500: keys that reached the quarantine terminal but were held back for
  // want of a live witness (see `anyStdioServerLive` below).
  const quarantineWithheld: string[] = [];
  // ENG-10500: THE WITNESS. Did this session manage to spawn ANY declared stdio
  // MCP child at all?
  //
  // `liveKeys` is already stdio-only (URL-based entries are skipped, because
  // Claude Code dials those directly and they have no child to find), so this is
  // exactly "at least one declared stdio server has a live child". An agent's
  // control plane - `augmented` and `direct-chat` - is always declared and always
  // ESSENTIAL, so in production a booted session always has a witness.
  const anyStdioServerLive = liveKeys.size > 0;
  // ENG-8062: keys that tripped the genuinely-dead floor and so overrode the
  // ENG-8013 storm-awareness suppression this tick.
  const genuinelyDead: string[] = [];
  for (const key of missing) {
    const sk = stateKey(codeName, key);
    const state = presenceReaperState.get(sk) ?? {
      attempts: 0,
      lastSeenLiveAt: null,
      lastAttemptedSessionStartedAt: null,
      gaveUpLogged: false,
      firstMissingAt: null,
      quarantineLogged: false,
      restartHistory: [],
      stableLiveSince: null,
      continuouslyMissingSince: null,
      deadChannelLogged: false,
      quarantineWithheldLogged: false,
      gaveUp: false,
    };
    // ENG-5932: stamp the start of this missing stretch (cleared on liveness in
    // the reset loop above). Drives the quarantine dwell floor. Set here — i.e.
    // only AFTER the cold-start grace returned early — so the dwell is measured
    // from "missing past cold start", which is exactly the floor we want.
    //
    // ENG-8062: the never-re-based continuous-missing clock. Set once when the
    // stretch begins and cleared ONLY by real liveness (the reset loop above),
    // so storm-awareness can't keep it perpetually young. Once it's older than
    // `deadChannelMs` the key is genuinely dead — a storm doesn't keep a channel
    // missing on every poll for 15 min without it ever coming live — and the
    // externally-started-generation excuse no longer applies.
    if (state.continuouslyMissingSince === null) {
      state.continuouslyMissingSince = nowMs;
    }
    const isGenuinelyDead = nowMs - state.continuouslyMissingSince >= deadChannelMs;
    // Suppress budget/dwell as churn collateral ONLY while the storm excuse
    // still holds (ENG-8013) — never once the key is genuinely dead (ENG-8062).
    const suppressAsChurn = currentGenerationExternallyStarted && !isGenuinelyDead;
    if (isGenuinelyDead) {
      genuinelyDead.push(key);
      if (currentGenerationExternallyStarted && !state.deadChannelLogged) {
        state.deadChannelLogged = true;
        log(
          `[mcp-presence-reaper] '${codeName}:${key}' continuously missing ${Math.round((nowMs - state.continuouslyMissingSince) / 60_000)}m (> ${Math.round(deadChannelMs / 60_000)}m) — genuinely dead, overriding storm-awareness so it can reach give-up/quarantine instead of churning (ENG-8062)`,
        );
      }
    }
    // ENG-8013 (storm-awareness): the dwell measures "continuously missing across
    // a STABLE session". When the current generation was born from an EXTERNAL
    // respawn (or a storm), re-base the dwell clock to now so it can only elapse
    // once the session has been churn-free for the full window, so a channel that's
    // missing purely as collateral of respawn churn never accrues dwell — but stop
    // re-basing once the key is genuinely dead so the dwell can finally elapse.
    if (state.firstMissingAt === null || suppressAsChurn) {
      state.firstMissingAt = nowMs;
    }
    // ENG-6480: the key is missing → its continuous-live stretch is broken, so
    // a prior brief green blip cannot accrue toward the stable-reset dwell.
    state.stableLiveSince = null;
    if (state.lastAttemptedSessionStartedAt !== sessionStartedAt) {
      // Advance the per-generation guard either way so we don't re-evaluate this
      // generation on later polls within it. But only a generation the reaper
      // itself restarted is a genuine failed recovery; an externally-started
      // generation is churn collateral (ENG-8013), so it must NOT increment the
      // give-up/quarantine budget or record a restart-history entry — UNLESS the
      // key is genuinely dead (ENG-8062), in which case we DO count it so it
      // reaches the give-up/quarantine terminal.
      state.lastAttemptedSessionStartedAt = sessionStartedAt;
      if (!suppressAsChurn) {
        state.attempts += 1;
        newGenerationKeys.add(key);
      }
    }
    // ENG-6480: prune the restart history to the rolling window, then derive
    // the rate-based give-up + escalating backoff from what remains.
    state.restartHistory = state.restartHistory.filter((t) => nowMs - t < BACKOFF_WINDOW_MS);
    const restartsInWindow = state.restartHistory.length;
    presenceReaperState.set(sk, state);
    const overAttemptsCap = state.attempts > MAX_PRESENCE_RESTART_ATTEMPTS;
    const overRateCap = restartsInWindow >= BACKOFF_GIVE_UP_RESTARTS;
    if (overAttemptsCap || overRateCap) {
      givenUp.push(key);
      state.gaveUp = true;
      if (!state.gaveUpLogged) {
        const cause = overAttemptsCap
          ? `${MAX_PRESENCE_RESTART_ATTEMPTS} consecutive failed restarts — declared but never recovers (likely orphaned config; see ENG-5279)`
          : `${restartsInWindow} restarts within ${Math.round(BACKOFF_WINDOW_MS / 60_000)}m — persistently flapping (crash-loop; ENG-6480)`;
        log(
          `[mcp-presence-reaper] giving up on '${codeName}:${key}' after ${cause}`,
        );
        state.gaveUpLogged = true;
        // ENG-5292: fire the give-up callback exactly once per
        // (codeName, serverKey). Manager-worker uses this to POST to
        // /host/integration-health so the broken MCP gets marked
        // unhealthy and dropped from the next .mcp.json regeneration.
        // Swallow errors — the reaper's own correctness must not
        // depend on the callback succeeding.
        if (onGiveUp) {
          try {
            onGiveUp(codeName, key);
          } catch (err) {
            log(
              `[mcp-presence-reaper] onGiveUp callback threw for '${codeName}:${key}' (suppressed; reaper continues): ${(err as Error).message}`,
            );
          }
        }
      }
      // ENG-5932: quarantine decision, folded into the give-up terminal state
      // rather than bolted on as a separate branch. A key is a quarantine
      // candidate only once it has (a) exhausted the restart budget — the same
      // ENG-5279 cap that put it in `givenUp` — AND (b) sat missing past the
      // dwell floor. Gated to OPTIONAL keys; ESSENTIAL (incl. everything
      // unclassified — fail closed) keeps the legacy declared-but-dead path,
      // so the agent's control-plane channels can never be quarantined.
      // Fires once per key (quarantineLogged), reset on liveness.
      if (
        quarantineMode !== 'off' &&
        !state.quarantineLogged &&
        classifyKey(key) === 'optional'
      ) {
        const dwellElapsed =
          quarantineDwellMs <= 0 ||
          (state.firstMissingAt !== null && nowMs - state.firstMissingAt >= quarantineDwellMs);
        // ENG-10500: the restart budget is SESSION-SCOPED, so on its own it
        // cannot tell "this channel died" from "this session never booted".
        //
        // `attempts` increments once per new `sessionStartedAt` while a key is
        // missing. A session that cannot come up at all - wedged on a Claude Code
        // login-picker or an unclaimed modal (ENG-9932) - spawns no MCP child, so
        // EVERY declared key is missing on EVERY generation and they all burn the
        // same budget in lockstep. They then reach this branch together, in one
        // pass, and each is quarantined for a fault none of them had.
        //
        // That is not hypothetical. On don (2026-09-13) slack and telegram were
        // quarantined 9ms apart - one reaper pass - while slack-channel-stderr.log
        // showed "Socket Mode connected" on EVERY cycle from 11:38 to 14:20, each
        // ended by a reaper SIGINT. The channels never failed once. The session was
        // wedged on a modal, and the reaper had ALREADY correctly decided (ENG-9932)
        // that a restart would not help - then quarantined the channels anyway.
        // 34 hours of silence and 13 dead-lettered inbounds.
        //
        // Two independent channels do not die 9ms apart. One session did.
        //
        // So require a WITNESS: some declared stdio MCP is live, i.e. this session
        // demonstrably CAN spawn children and simply did not spawn this one. With a
        // witness, "missing past the restart budget" really is channel death and
        // ENG-5932's remedy applies. Without one, the evidence is about the session.
        //
        // This gate sits ABOVE ENG-8062's genuinely-dead override on purpose: a
        // channel a wedged session never gave a chance to spawn is continuously
        // missing too, so that floor trips for all of them at once and reproduces
        // the same signature. A channel cannot be shown dead by a session that
        // never started it.
        //
        // WITHHOLDING IS NOT TERMINAL. `quarantineLogged` stays false, so once the
        // session boots - a human clears the modal, `augmented` comes live - a
        // channel that is still missing quarantines on a later tick exactly as
        // ENG-5932 intends. The ENG-5279 orphaned-config case this was built for
        // (Kylie's msteams, in an otherwise healthy session) always has a witness.
        if (dwellElapsed && !anyStdioServerLive) {
          quarantineWithheld.push(key);
          if (!state.quarantineWithheldLogged) {
            state.quarantineWithheldLogged = true;
            log(
              `[mcp-presence-reaper] '${codeName}:${key}' given up but NOT quarantined - no declared stdio MCP server is live, so the restart budget was exhausted by a session that cannot spawn anything, not by this channel dying. Check the pane (tmux attach -t agt-${codeName}); the channel stays provisioned and will quarantine on a later tick if it is still missing once the session is up (ENG-10500)`,
            );
          }
        } else if (dwellElapsed) {
          state.quarantineLogged = true;
          if (quarantineMode === 'enforce') {
            quarantined.push(key);
            log(
              `[mcp-presence-reaper] QUARANTINE '${codeName}:${key}' — optional channel dead past restart budget + ${quarantineDwellMs}ms dwell; dropping from provisioned set so the dead channel stops restarting the whole session (ENG-5932)`,
            );
            if (onQuarantine) {
              try {
                onQuarantine(codeName, key);
              } catch (err) {
                log(
                  `[mcp-presence-reaper] onQuarantine callback threw for '${codeName}:${key}' (suppressed; reaper continues): ${(err as Error).message}`,
                );
              }
            }
          } else {
            // shadow — observe only; legacy give-up behaviour is unchanged.
            wouldQuarantine.push(key);
            log(
              `[mcp-presence-reaper] SHADOW would-quarantine '${codeName}:${key}' — optional channel dead past restart budget + ${quarantineDwellMs}ms dwell; no action taken (ENG-5932 shadow mode)`,
            );
          }
        }
      }
    } else {
      // ENG-6480: under both give-up ceilings — decide whether to restart now
      // or back off. The first BACKOFF_AFTER_RESTARTS restarts are immediate
      // (backoffMs = 0), preserving the orphan/transient path exactly; beyond
      // that the interval doubles (capped at BACKOFF_MAX_MS) so a crash-looper
      // is spaced out instead of bouncing every poll.
      const backoffMs =
        restartsInWindow < BACKOFF_AFTER_RESTARTS
          ? 0
          : Math.min(
              BACKOFF_BASE_MS * 2 ** (restartsInWindow - BACKOFF_AFTER_RESTARTS),
              BACKOFF_MAX_MS,
            );
      const lastRestartAt = state.restartHistory[state.restartHistory.length - 1] ?? null;
      if (lastRestartAt !== null && nowMs - lastRestartAt < backoffMs) {
        backingOff.push(key);
      } else {
        active.push(key);
      }
    }
  }

  if (active.length === 0) {
    // No missing key is eligible to drive a restart this tick. Distinguish
    // the two no-op causes: at least one key is merely waiting out its
    // backoff interval ('backoff', ENG-6480) vs every key has hit a give-up
    // ceiling ('all-keys-over-cap'). Stays quiet on repeat give-up ticks
    // (gaveUpLogged guard); logs once per backoff hold so the spacing is
    // visible without spamming.
    const reason: 'backoff' | 'all-keys-over-cap' =
      backingOff.length > 0 ? 'backoff' : 'all-keys-over-cap';
    if (backingOff.length > 0) {
      log(
        `[mcp-presence-reaper] '${codeName}': holding off restart for [${backingOff.join(', ')}] — escalating backoff interval not yet elapsed (ENG-6480)${
          givenUp.length > 0 ? ` (given up: [${givenUp.join(', ')}])` : ''
        }`,
      );
    }
    return {
      missing,
      restarted: false,
      reason,
      givenUp,
      backingOff: backingOff.length > 0 ? backingOff : undefined,
      rotationGraced: rotationGraced.length > 0 ? rotationGraced : undefined,
      rotationProbeArmed: rotationProbeArmed.length > 0 ? rotationProbeArmed : undefined,
      rotationProbeWaiting: rotationProbeWaiting.length > 0 ? rotationProbeWaiting : undefined,
      rotationProbeExhausted:
        rotationProbeExhausted.length > 0 ? rotationProbeExhausted : undefined,
      rotationRespawnOutcomes:
        Object.keys(rotationRespawnOutcomes).length > 0 ? rotationRespawnOutcomes : undefined,
      quarantined: quarantined.length > 0 ? quarantined : undefined,
      wouldQuarantine: wouldQuarantine.length > 0 ? wouldQuarantine : undefined,
      genuinelyDead: genuinelyDead.length > 0 ? genuinelyDead : undefined,
      quarantineWithheld: quarantineWithheld.length > 0 ? quarantineWithheld : undefined,
    };
  }

  // ENG-9932: THE RESTART IS DECIDED — last check before we spend it.
  //
  // `declared MCP(s) have no live children` is a symptom, and it has a second
  // cause that wants the opposite remedy. If Claude Code never finished
  // booting because it is sitting on a modal, it never spawned an MCP child at
  // all, and every restart re-presents the same modal. On agt-aws-1
  // (2026-09-03) that loop ran three times in ten minutes for each of eight
  // agents and paused all of them via the ENG-5441 breaker. The MCP servers
  // were healthy the entire time; marlow's pane was on the login picker.
  //
  // So: before tearing the session down, ask whether a fresh session could
  // possibly come up. `classifyUnanswerablePane` answers that for the three
  // states where it provably cannot (see its doc for why an ANSWERABLE dialog
  // is deliberately excluded — a respawn's `acceptDialogs()` cascade really
  // does clear those, so they keep the legacy path).
  //
  // Failing open is the invariant here. No `readPane`, an unreadable pane, a
  // throwing capture, or an unrecognised pane all fall through to the restart
  // below. The cost of a wrong hold (MCP recovery silently suppressed
  // fleet-wide) is far worse than the cost of a wrong restart (the status quo).
  if (readPane) {
    let pane: string | null = null;
    try {
      pane = readPane(codeName);
    } catch (err) {
      // Fail OPEN — see above. A tmux hiccup is not evidence of a blocked pane.
      log(
        `[mcp-presence-reaper] '${codeName}': pane read threw (${(err as Error).message}) — proceeding with restart (ENG-9932 fails open)`,
      );
    }
    const blocked = classifyUnanswerablePane(pane);
    if (blocked) {
      // Content-free by construction: a hash + a length, never pane text.
      // The pane may hold the customer message the agent was mid-way through
      // answering, and this value is persisted into a control-plane alert.
      const fingerprint = pane ? summarizeUnanswerablePane(pane) : null;
      // Say the true thing, in the vocabulary of the actual fault. The old line
      // named MCP servers, which sent the on-call to MCP wiring; this one names
      // the modal and says restarting cannot fix it.
      log(
        `[mcp-presence-reaper] '${codeName}': NOT restarting — session is blocked on an interactive prompt (${blocked}), ` +
          `so declared MCP(s) [${active.join(', ')}] were never spawned rather than having died. ` +
          `A restart re-presents the same prompt and burns the ENG-5441 breaker budget. ` +
          `Needs a human at the pane: tmux attach -t agt-${codeName}` +
          // Fingerprint, never pane text — see summarizeUnanswerablePane.
          (fingerprint ? ` (modal fp=${fingerprint.hash} len=${fingerprint.length})` : ''),
      );
      if (onPaneBlocked) {
        try {
          onPaneBlocked(codeName, {
            kind: blocked,
            activeKeys: [...active],
            paneHash: fingerprint?.hash ?? null,
            paneLength: fingerprint?.length ?? null,
          });
        } catch (err) {
          log(
            `[mcp-presence-reaper] onPaneBlocked callback threw for '${codeName}' (suppressed): ${(err as Error).message}`,
          );
        }
      }
      // Deliberately does NOT touch `restartHistory`, does NOT call `onRestart`
      // and does NOT call `stopSession`. A restart we correctly declined to
      // make must not consume the backoff budget or the breaker budget — the
      // whole point is that the agent stays recoverable by the human this line
      // just paged, instead of arriving at them already auto-paused.
      return {
        missing,
        restarted: false,
        reason: 'pane-blocked-on-dialog',
        paneBlockedOn: blocked,
        givenUp: givenUp.length > 0 ? givenUp : undefined,
        backingOff: backingOff.length > 0 ? backingOff : undefined,
        rotationGraced: rotationGraced.length > 0 ? rotationGraced : undefined,
        rotationProbeArmed: rotationProbeArmed.length > 0 ? rotationProbeArmed : undefined,
        rotationProbeWaiting: rotationProbeWaiting.length > 0 ? rotationProbeWaiting : undefined,
        rotationProbeExhausted:
          rotationProbeExhausted.length > 0 ? rotationProbeExhausted : undefined,
        rotationRespawnOutcomes:
          Object.keys(rotationRespawnOutcomes).length > 0 ? rotationRespawnOutcomes : undefined,
        quarantined: quarantined.length > 0 ? quarantined : undefined,
        wouldQuarantine: wouldQuarantine.length > 0 ? wouldQuarantine : undefined,
        genuinelyDead: genuinelyDead.length > 0 ? genuinelyDead : undefined,
        quarantineWithheld: quarantineWithheld.length > 0 ? quarantineWithheld : undefined,
      };
    }
  }

  // Audit line: emit the missing key(s) so we can spot patterns ("xero
  // crashes every 4h") without grepping pane logs. Single line per
  // restart event by design. Mention given-up keys too so the operator
  // sees the full picture.
  const givenUpSuffix = givenUp.length > 0
    ? ` (skipping over-cap: [${givenUp.join(', ')}])`
    : '';
  log(
    `[mcp-presence-reaper] '${codeName}': declared MCP(s) [${active.join(', ')}] have no live children — restarting session${givenUpSuffix}`,
  );
  // ENG-5286: fire the restart-observer callback before stopSession so
  // the API records the event with the exact key set that prompted the
  // restart. Errors are swallowed — reaper correctness wins.
  if (onRestart) {
    try {
      // CodeRabbit on PR #1186: callbacks may be sync OR async.
      // The synchronous try/catch catches throws from sync callbacks
      // and the synchronous part of async ones; the .catch() below
      // catches rejections from async callbacks so they don't escape
      // as unhandled promise rejections.
      const maybePromise = onRestart(codeName, { activeKeys: active, givenUpKeys: givenUp });
      if (
        maybePromise &&
        typeof (maybePromise as PromiseLike<void>).then === 'function'
      ) {
        void (maybePromise as PromiseLike<void>).then(undefined, (err: unknown) => {
          log(
            `[mcp-presence-reaper] onRestart callback rejected for '${codeName}' (suppressed; restart proceeds): ${(err as Error).message}`,
          );
        });
      }
    } catch (err) {
      log(
        `[mcp-presence-reaper] onRestart callback threw for '${codeName}' (suppressed; restart proceeds): ${(err as Error).message}`,
      );
    }
  }
  stopSession(codeName, { activeKeys: active });
  // ENG-6480: record this restart against each key that drove it, so the
  // window count grows and the escalating backoff / rate give-up engage on
  // the next ticks. Only count it once per fresh session generation — a slow
  // respawn produces several same-generation polls that all (idempotently)
  // call stopSession, but they are ONE restart cycle, not many. (A key still
  // in `backingOff` did not drive this restart, so it isn't recorded — it gets
  // respawned by the global restart anyway.)
  for (const key of active) {
    if (!newGenerationKeys.has(key)) continue;
    const st = presenceReaperState.get(stateKey(codeName, key));
    if (st) st.restartHistory.push(nowMs);
  }
  return {
    missing,
    restarted: true,
    givenUp: givenUp.length > 0 ? givenUp : undefined,
    backingOff: backingOff.length > 0 ? backingOff : undefined,
    rotationGraced: rotationGraced.length > 0 ? rotationGraced : undefined,
    rotationProbeArmed: rotationProbeArmed.length > 0 ? rotationProbeArmed : undefined,
    rotationProbeWaiting: rotationProbeWaiting.length > 0 ? rotationProbeWaiting : undefined,
    rotationProbeExhausted:
      rotationProbeExhausted.length > 0 ? rotationProbeExhausted : undefined,
    rotationRespawnOutcomes:
      Object.keys(rotationRespawnOutcomes).length > 0 ? rotationRespawnOutcomes : undefined,
    quarantined: quarantined.length > 0 ? quarantined : undefined,
    wouldQuarantine: wouldQuarantine.length > 0 ? wouldQuarantine : undefined,
    genuinelyDead: genuinelyDead.length > 0 ? genuinelyDead : undefined,
    quarantineWithheld: quarantineWithheld.length > 0 ? quarantineWithheld : undefined,
  };
}
